/**
 * Client-only gate for the post-analysis scan animation.
 *
 * Storage shape: { [reviewEntityId]: playedAtIso }
 * Play when the entity is < 24h old and we have not recorded a play yet.
 * No DB column needed — created_at comes from the API; plays live in localStorage.
 */

const STORAGE_KEY = 'petal_scan_animation_played_v1'
const PLAY_WINDOW_MS = 24 * 60 * 60 * 1000
const PRUNE_AFTER_MS = 48 * 60 * 60 * 1000

const readMap = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

const writeMap = (map) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map))
  } catch {
    // Quota / private mode — ignore; animation may replay once.
  }
}

const prune = (map) => {
  const cutoff = Date.now() - PRUNE_AFTER_MS
  const next = {}
  for (const [id, playedAt] of Object.entries(map)) {
    const ts = Date.parse(playedAt)
    if (Number.isFinite(ts) && ts >= cutoff) next[id] = playedAt
  }
  return next
}

export const hasPlayedScanAnimation = (reviewEntityId) => {
  if (reviewEntityId == null) return false
  const map = readMap()
  return !!map[String(reviewEntityId)]
}

export const markScanAnimationPlayed = (reviewEntityId) => {
  if (reviewEntityId == null) return
  const map = prune(readMap())
  map[String(reviewEntityId)] = new Date().toISOString()
  writeMap(map)
}

/** Admin-only escape hatch: clear the recorded play so the animation is eligible again. */
export const clearScanAnimationPlayed = (reviewEntityId) => {
  if (reviewEntityId == null) return
  const map = readMap()
  if (!(String(reviewEntityId) in map)) return
  delete map[String(reviewEntityId)]
  writeMap(map)
}

/** True when the brand is still in its first-day window and hasn't played yet. */
export const shouldPlayScanAnimation = (reviewEntityId, createdAt) => {
  if (reviewEntityId == null || !createdAt) return false
  const createdMs = Date.parse(createdAt)
  if (!Number.isFinite(createdMs)) return false
  if (Date.now() - createdMs > PLAY_WINDOW_MS) return false
  return !hasPlayedScanAnimation(reviewEntityId)
}
